import React from "react";
import { View } from "@amzn/stencil-react-components/layout";
import { Text, H1, H3, H4 } from "@amzn/stencil-react-components/text";
import { Input } from "@amzn/stencil-react-components/input";
import { FormWrapper } from "@amzn/stencil-react-components/form";
import { ScreenReaderOnly } from "@amzn/stencil-react-components/screen-reader-only";
import WhiteboardLadyImage from "../../images/Whiteboard_Lady.svg";
import HijabLadyImage from "../../images/Hijab_Lady.svg";

interface ConsentFormProps {
  consentDecision: "accept" | "decline" | null;
  onConsentDecisionChange: (decision: "accept" | "decline") => void;

  // For guest page - text input validation
  showTextInput?: boolean;
  consentText?: string;
  onConsentTextChange?: (text: string) => void;
  validationError?: string | null;
  requiredConsentText?: string;
  isAcceptEnabled?: boolean;
}

export const ConsentForm: React.FC<ConsentFormProps> = ({
  consentDecision,
  onConsentDecisionChange,
  showTextInput = false,
  consentText = "",
  onConsentTextChange,
  validationError,
  requiredConsentText = "I Consent",
  isAcceptEnabled = true,
}) => {
  return (
    <View className="consent-content">
      {/* Main Title */}
      <View className="consent-content__header">
        <H1 id="consent-form-title" className="main-title">
          Do you consent to the use of a note-taking tool?
        </H1>
        <Text className="required-field-indicator">
          * indicates a required field
        </Text>
      </View>

      {/* Content Section */}
      <View className="consent-content__body">
        {/* Left Content */}
        <View className="consent-content__text">
          <H3 className="consent-text consent-text--section-title">
            Meeting Transcript and Data Storage Consent
          </H3>

          <Text className="consent-text" style={{ paddingBottom: "1rem" }}>
            As part of this investigation, we may need to record and store
            meeting transcripts and related data. This information will be used
            solely for the purpose of conducting a thorough and accurate
            investigation.
          </Text>

          <H4 className="consent-text consent-text--bold">What we collect:</H4>
          <ul className="consent-list">
            <li className="list-item">
              Meeting audio recordings and transcripts
            </li>
            <li className="list-item">Meeting notes and documentation</li>
            <li className="list-item">
              Related investigation materials and correspondence
            </li>
          </ul>

          <H4 className="consent-text consent-text--bold">
            How we use this information:
          </H4>
          <ul className="consent-list">
            <li className="list-item">
              To ensure accurate documentation of the investigation process
            </li>
            <li className="list-item">
              To maintain a complete record for legal and compliance purposes
            </li>
            <li className="list-item">
              To enable proper review and analysis of investigation findings
            </li>
          </ul>

          <H4 className="consent-text consent-text--bold">Data protection:</H4>
          <Text className="consent-text">
            All recorded information will be stored securely and accessed only
            by authorized personnel involved in the investigation. Data will be
            retained in accordance with Amazon's data retention policies and
            applicable legal requirements.
          </Text>
        </View>

        {/* Right Illustration */}
        <View className="consent-content__illustration">
          <View className="illustration-container">
            <img
              src={WhiteboardLadyImage}
              alt=""
              role="presentation"
              width="100"
              height="auto"
            />
            <img
              src={HijabLadyImage}
              alt=""
              role="presentation"
              width="120"
              height="auto"
            />
          </View>
        </View>
      </View>

      {/* Spacer for 3 line breaks */}
      <View className="consent-form__spacer" />

      {/* Consent Options */}
      <View className="consent-form">
        <FormWrapper>
          <fieldset className="consent-form__fieldset">
            <legend className="consent-form__legend">
              Do you consent to above? *
            </legend>

            <View className="consent-form__options">
              {/* Yes Option */}
              <label
                className={`consent-form__option ${
                  consentDecision === "accept"
                    ? "consent-form__option--selected"
                    : ""
                } ${
                  showTextInput && !isAcceptEnabled
                    ? "consent-form__option--disabled"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="consent-decision"
                  value="accept"
                  checked={consentDecision === "accept"}
                  onChange={() => onConsentDecisionChange("accept")}
                  disabled={showTextInput && !isAcceptEnabled}
                  aria-describedby={
                    validationError ? "consent-error" : undefined
                  }
                  className="consent-form__radio-input"
                />
                <View className="consent-form__radio">
                  <Text className="radio-label">Yes, I consent</Text>
                  <View
                    className={`radio-button ${
                      consentDecision === "accept"
                        ? "radio-button--selected"
                        : ""
                    }`}
                  />
                </View>
              </label>

              {/* No Option */}
              <label
                className={`consent-form__option ${
                  consentDecision === "decline"
                    ? "consent-form__option--selected"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="consent-decision"
                  value="decline"
                  checked={consentDecision === "decline"}
                  onChange={() => onConsentDecisionChange("decline")}
                  aria-describedby={
                    validationError ? "consent-error" : undefined
                  }
                  className="consent-form__radio-input"
                />
                <View className="consent-form__radio">
                  <Text className="radio-label">No, I don't consent</Text>
                  <View
                    className={`radio-button ${
                      consentDecision === "decline"
                        ? "radio-button--selected"
                        : ""
                    }`}
                  />
                </View>
              </label>
            </View>

            {validationError && (
              <div
                id="consent-error"
                role="alert"
                className="error-message"
                aria-live="assertive"
              >
                <ScreenReaderOnly>Validation error: </ScreenReaderOnly>
                {validationError}
              </div>
            )}
          </fieldset>
        </FormWrapper>
      </View>

      {/* Text Input for Guest Validation */}
      {showTextInput && (
        <View className="consent-form__text-input">
          <label htmlFor="consent-text-input" className="input-label">
            Type exactly "{requiredConsentText}" for confirmation *
          </label>
          <Input
            id="consent-text-input"
            className={`text-input ${validationError ? "text-input--error" : ""}`}
            value={consentText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onConsentTextChange?.(e.target.value)
            }
            placeholder={`Type "${requiredConsentText}" here`}
            aria-describedby={
              validationError ? "consent-text-error" : undefined
            }
            aria-invalid={validationError ? "true" : "false"}
            aria-required="true"
            width="100%"
          />
          {validationError && (
            <div
              id="consent-text-error"
              role="alert"
              className="error-message"
              aria-live="assertive"
            >
              <ScreenReaderOnly>Validation error: </ScreenReaderOnly>
              {validationError}
            </div>
          )}
        </View>
      )}
    </View>
  );
};

export default ConsentForm;
